import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import {
  generateImageGeneratorPattern,
  generateImageGeneratorStyle,
  getImageGeneratorAssetPreview,
  getImageGeneratorForecastReports,
  getImageGeneratorSurfacesCatalog,
  ImageGeneratorCatalogAsset,
  ImageGeneratorCatalogColourStory,
  ImageGeneratorCatalogSurfaceSlide,
  ImageGeneratorHistoryRecord,
  ImageGeneratorPatternChoice,
  regenerateImageGenerator,
  getImageGeneratorErrorMessage,
  ImageGeneratorGenerateStylePayload,
} from "../../app/api/imageGenerator";
import ImageGeneratorInlineResult from "./ImageGeneratorInlineResult";
import ImageGeneratorTracker from "./ImageGeneratorTracker";
import {
  MAKE_OWN_DEFAULT_SLOTS,
  MAKE_OWN_MAX_SLOTS,
  emptyMakeOwnSlots,
} from "./ImageGeneratorMakeOwnPicker";
import ImageGeneratorPreferenceTile from "./ImageGeneratorPreferenceTile";
import ImageGeneratorStyleFields from "./ImageGeneratorStyleFields";
import ImageGeneratorSpecificationUpload from "./ImageGeneratorSpecificationUpload";
import ImageGeneratorPaletteSection from "./ImageGeneratorPaletteSection";
import ImageGeneratorDenimSections from "./ImageGeneratorDenimSections";
import ImageGeneratorPatternSpecs from "./ImageGeneratorPatternSpecs";
import ImageGeneratorEmbroideryType from "./ImageGeneratorEmbroideryType";
import ImageGeneratorForecastSilhouettePicker from "./ImageGeneratorForecastSilhouettePicker";
import ImageGeneratorSlideReports from "./ImageGeneratorSlideReports";
import ImageGeneratorReportSlider from "./ImageGeneratorReportSlider";
import {
  reportSlidesForItem,
  reportSlidesForMacroThemeColour,
} from "./ImageGeneratorReportSlides";
import {
  ExclusivePills,
  FieldBlock,
  FieldSelect,
  RequiredMark,
  chipClassName,
  fieldsRowClassName,
  formFieldsShellClassName,
  helperTextClassName,
  sectionClassName,
  sectionTitleClassName,
} from "./ImageGeneratorControls";
import {
  mutedButtonClassName,
  outlineButtonClassName,
  PREFERENCE_PAGE_SIZE,
  submitButtonClassName,
} from "./collectionBuildConstants";
import { ImageGeneratorResultItem } from "./ImageGeneratorDesignDetails";
import { useImageGeneratorJobs } from "./ImageGeneratorJobsContext";
import { useGenerativeDenimCatalog } from "./shared/useGenerativeCatalog";
import { useGenerativeDenimPreviews } from "./shared/useGenerativeDenimPreviews";
import {
  extractGenerativePalette,
  mapExtractedPalette,
  paletteExtractFileError,
} from "./shared/generativeCatalog";
import {
  useImageGeneratorCatalog,
  ImageGeneratorPendingSession,
} from "./useImageGeneratorCatalog";
import {
  IMAGE_GENERATOR_SESSION_PARAM,
  imageGeneratorRootId,
  imageGeneratorSessionChain,
  mapImageGeneratorHistoryRecord,
} from "./imageGeneratorSession";
import { buildImageGeneratorBasePayload } from "./imageGeneratorPayload";
import {
  MARKETS,
  CATEGORIES,
  GARMENT_FORMATS,
  PATTERN_PROCESSES,
  PRODUCT_TYPES,
  STARTING_DIRECTION_OPTIONS,
  categoryDisplayLabel,
  FORECAST_REPORT_COPY,
  INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_BODY,
  INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_TITLE,
  UPLOAD_INFLUENCE,
  getImageGeneratorFollowUpFields,
  getLengthOptions,
  isDenimEligibleCategory,
  isDenimEligibleProductType,
  isForecastInfluence,
  isLibraryInfluence,
  isMaterialLengthOnlyInfluence,
  isUploadInfluence,
  styleOptionsFor,
  ImageGeneratorStartingDirection,
  SPECIFICATION_IMAGE_PENDING_ERROR,
} from "../../constants/imageGeneratorDictionaries";

const viewReportButtonClassName =
  "mt-3 inline-flex h-8 items-center justify-center rounded-[8px] border border-[#737373] bg-white px-3 py-1 text-xs leading-none text-secondary transition-colors hover:bg-gray";

const PATTERN_CHOICE_OPTIONS: Array<{
  label: string;
  value: ImageGeneratorPatternChoice;
}> = [
  { label: "I will choose", value: "choose" },
  { label: "You suggest", value: "recommend" },
];

/** Silhouette required when that deck has slides; detailed is optional. */
const forecastSketchSelectionSatisfied = (
  silhouetteCount: number,
  _detailCount: number,
  silhouetteId: string,
  _detailId: string
) => {
  if (silhouetteCount > 0 && !silhouetteId) {
    return false;
  }
  return true;
};

const forecastSketchMissingMessage = (
  silhouetteCount: number,
  _detailCount: number,
  silhouetteId = "",
  _detailId = ""
) => {
  if (silhouetteCount > 0 && !silhouetteId) {
    return FORECAST_REPORT_COPY.silhouetteRequired;
  }
  return FORECAST_REPORT_COPY.sketchRequired;
};

const PATTERN_PROCESS_OPTIONS = PATTERN_PROCESSES.map(item => ({
  value: item,
  label:
    item === "Screen"
      ? "Screen Print"
      : item === "Digital"
        ? "Digital Print"
        : item,
}));

type CustomPalette = {
  hexes: string[];
  title: string;
  swatches?: Array<{
    hex: string;
    name: string;
    tier: "major" | "minor";
  }>;
};

const productOptionsFor = (marketValue: string, categoryValue: string) =>
  PRODUCT_TYPES[marketValue]?.[categoryValue] ||
  PRODUCT_TYPES[marketValue]?.["Western RTW"] || [
    "Dresses",
    "Kurtas",
    "Tops",
    "Trousers",
  ];

type Props = {
  isSidebarOpen: boolean;
};

const ImageGenerator: React.FC<Props> = ({ isSidebarOpen }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessions, trackJob, updateSession, loadSession, openSession } =
    useImageGeneratorJobs();
  const sessionParam = Number(
    searchParams.get(IMAGE_GENERATOR_SESSION_PARAM) || ""
  );
  const activeSessionId =
    Number.isInteger(sessionParam) && sessionParam > 0 ? sessionParam : null;
  const sessionLocked = Boolean(activeSessionId);
  const missingSessionId = useRef<number | null>(null);
  const loadingSessionId = useRef<number | null>(null);
  const lastOpenedSessionId = useRef<number | null>(null);
  const [activeTab, setActiveTab] = useState<"query" | "tracker">("query");

  useEffect(() => {
    if (activeSessionId && activeSessionId !== lastOpenedSessionId.current) {
      setActiveTab("query");
    }
    lastOpenedSessionId.current = activeSessionId;
  }, [activeSessionId]);

  const genre = "Fashion";
  const [seasonId, setSeasonId] = useState<string>("");
  const [market, setMarket] = useState<string>("");
  const [category, setCategory] = useState<string>("");

  const [outputType, setOutputType] = useState<"Style" | "Pattern" | "">("");
  const [selectedMacroThemeId, setSelectedMacroThemeId] = useState<string>("");
  const [selectedColourStoryId, setSelectedColourStoryId] =
    useState<string>("");
  const [selectedPatternTrendId, setSelectedPatternTrendId] =
    useState<string>("");
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<number>(-1);
  const [customPalettes, setCustomPalettes] = useState<CustomPalette[]>([]);
  const [makeOwnOpen, setMakeOwnOpen] = useState(false);
  const [makeOwnSlots, setMakeOwnSlots] =
    useState<Array<string | null>>(emptyMakeOwnSlots);
  const [makeOwnSlotIndex, setMakeOwnSlotIndex] = useState(0);
  const [makeOwnEditingIndex, setMakeOwnEditingIndex] = useState<number | null>(
    null
  );
  const [paletteExtracting, setPaletteExtracting] = useState(false);
  const makeOwnRef = useRef<HTMLDivElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [patternChoice, setPatternChoice] = useState<
    ImageGeneratorPatternChoice | ""
  >("");
  const [trendSource, setTrendSource] = useState<"pattern" | "surface" | "">(
    ""
  );
  const [surfaceSlides, setSurfaceSlides] = useState<
    ImageGeneratorCatalogSurfaceSlide[]
  >([]);
  const [surfacesLoading, setSurfacesLoading] = useState(false);
  const [selectedSurfaceId, setSelectedSurfaceId] = useState("");
  const [surfacePage, setSurfacePage] = useState(1);
  const [forecastSilhouetteSlides, setForecastSilhouetteSlides] = useState<
    ImageGeneratorCatalogSurfaceSlide[]
  >([]);
  const [forecastDetailSlides, setForecastDetailSlides] = useState<
    ImageGeneratorCatalogSurfaceSlide[]
  >([]);
  const [forecastReportsLoading, setForecastReportsLoading] = useState(false);
  const [forecastSilhouetteId, setForecastSilhouetteId] = useState("");
  const [forecastDetailId, setForecastDetailId] = useState("");
  const [forecastSketchOpen, setForecastSketchOpen] = useState(false);
  const [forecastSketchAutoOpenToken, setForecastSketchAutoOpenToken] =
    useState(0);
  const [forecastSketchAutoOpenSeen, setForecastSketchAutoOpenSeen] =
    useState(0);
  const [showColourReport, setShowColourReport] = useState(false);
  const [showMicroReport, setShowMicroReport] = useState(false);
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [preferencePage, setPreferencePage] = useState<number>(1);
  const [assetPreviews, setAssetPreviews] = useState<Record<string, string>>(
    {}
  );

  const [garmentFormat, setGarmentFormat] = useState<string>("");
  const [selectedEmbroideryType, setSelectedEmbroideryType] =
    useState<string>("");
  const [patternRepeat, setPatternRepeat] = useState<string>("");
  const [patternFollowUp, setPatternFollowUp] = useState<string>("");
  const [patternFollowUpText, setPatternFollowUpText] = useState<string>("");
  const [patternOffset, setPatternOffset] = useState<string>("");
  const [patternDensity, setPatternDensity] = useState<string>("");
  const [patternScale, setPatternScale] = useState<string>("");
  const [patternColorCount, setPatternColorCount] = useState<string>("");
  const [patternProcess, setPatternProcess] = useState<string>("");

  const [influence, setInfluence] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [styleType, setStyleType] = useState<string>("");
  const [material, setMaterial] = useState<string>("");
  const [sleeve, setSleeve] = useState<string>("");
  const [neckline, setNeckline] = useState<string>("");
  const [length, setLength] = useState<string>("");
  const [fit, setFit] = useState<string>("");
  const [wash, setWash] = useState<string>("");
  const [specificationImageUrl, setSpecificationImageUrl] = useState("");
  const [specificationActive, setSpecificationActive] = useState(false);
  const [specificationUploading, setSpecificationUploading] = useState(false);
  const [specificationResetKey, setSpecificationResetKey] = useState(0);
  const [confirmIncompleteDropdowns, setConfirmIncompleteDropdowns] =
    useState(false);
  const specificationSessionLockedRef = useRef(false);
  const [startingDirection, setStartingDirection] = useState<
    ImageGeneratorStartingDirection | ""
  >("");
  const [denimMaterialId, setDenimMaterialId] = useState<string>("");
  const [denimWashId, setDenimWashId] = useState<string>("");
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");

  const [busy, setBusy] = useState<"generate" | "regen" | null>(null);
  const [resultItems, setResultItems] = useState<ImageGeneratorResultItem[]>(
    []
  );
  const [selectedResultId, setSelectedResultId] = useState<string>("");
  const [resultScrollKey, setResultScrollKey] = useState(0);
  const hydratedSessionId = useRef<number | null>(null);
  const assetPreviewCache = useRef<Map<string, string>>(new Map());
  const pendingSessionRef = useRef<ImageGeneratorPendingSession | null>(null);
  const {
    catalog,
    setCatalog,
    catalogLoading,
    seasons,
    setSeasons,
    seasonsLoading,
    seasonLabel,
    hasReportThemes,
  } = useImageGeneratorCatalog({
    market,
    category,
    seasonId,
    setSeasonId,
    genre,
    sessionLocked,
    pendingSessionRef,
    setSelectedMacroThemeId,
    setSelectedColourStoryId,
    setSelectedPatternTrendId,
    setSelectedPaletteIndex,
    setSelectedPreferences,
  });
  const denimCatalog = useGenerativeDenimCatalog();
  const denimTiles = useMemo(
    () => [...denimCatalog.materials, ...denimCatalog.washes],
    [denimCatalog.materials, denimCatalog.washes]
  );

  const denimAllowed = isDenimEligibleCategory(category);
  const isDenimPath =
    outputType === "Style" && startingDirection === "denim" && denimAllowed;
  /** Uploaded specification reference or its preview. Hidden style values stay. */
  const specificationImageActive =
    Boolean(specificationImageUrl) || specificationActive;
  const denimPreviews = useGenerativeDenimPreviews(denimTiles, isDenimPath);
  const productTypeOptions = productOptionsFor(market, category);
  const styleTypeOptions = styleOptionsFor(market, productType, category);
  const lengthOptions = getLengthOptions(productType);
  const followUpFields = getImageGeneratorFollowUpFields(
    market,
    category,
    productType,
    influence,
    startingDirection
  );
  /**
   * Follow-ups for the selected Influence. StyleFields hides them until
   * Influence is set (progressive first row). Field set per influence is Task 3.
   */
  const lookFollowUpFields = getImageGeneratorFollowUpFields(
    market,
    category,
    productType,
    influence,
    startingDirection
  );
  const showFollowUp = (
    field: "neckline" | "sleeve" | "material" | "fit" | "wash" | "length"
  ) => followUpFields.includes(field);
  const isFollowUpFilled = (
    field: "neckline" | "sleeve" | "material" | "fit" | "wash" | "length"
  ) => {
    switch (field) {
      case "material":
        return Boolean(material);
      case "sleeve":
        return Boolean(sleeve);
      case "neckline":
        return Boolean(neckline);
      case "length":
        return Boolean(length);
      case "fit":
        return Boolean(fit);
      case "wash":
        return Boolean(wash);
      default:
        return true;
    }
  };
  const denimColourStories: ImageGeneratorCatalogColourStory[] =
    denimCatalog.palettes.map(palette => ({
      id: palette.id,
      title: palette.label,
      palette: palette.hexes.map(hex => ({ hex, tier: "major" as const })),
    }));

  useEffect(() => {
    const products = productOptionsFor(market, category);
    if (sessionLocked || !market || !category || !products.length) {
      return;
    }
    if (productType && !products.includes(productType)) {
      setProductType("");
      setStyleType("");
    }
  }, [market, category, productType, sessionLocked]);

  useEffect(() => {
    const styles = styleOptionsFor(market, productType, category);
    if (sessionLocked || !productType || !styles.length) {
      return;
    }
    if (styleType && !styles.includes(styleType)) {
      setStyleType("");
    }
  }, [market, category, productType, styleType, sessionLocked]);

  useEffect(() => {
    const wasLocked = specificationSessionLockedRef.current;
    specificationSessionLockedRef.current = sessionLocked;
    if (!wasLocked && sessionLocked) {
      setSpecificationResetKey(key => key + 1);
    }
  }, [sessionLocked]);

  useEffect(() => {
    if (sessionLocked) {
      return;
    }
    setOutputType("");
    setProductType("");
    setStyleType("");
    setMaterial("");
    setSleeve("");
    setNeckline("");
    setFit("");
    setWash("");
    setPatternChoice("");
    setTrendSource("");
    setSelectedSurfaceId("");
    setSurfacePage(1);
    setAdditionalPrompt("");
    setStartingDirection("");
    setDenimMaterialId("");
    setDenimWashId("");
    setLength("");
    setSpecificationImageUrl("");
    setSpecificationActive(false);
    setSpecificationUploading(false);
    setSpecificationResetKey(key => key + 1);
    // Required pill fields — never carry prior selections into a new brief.
    setGarmentFormat("");
    setSelectedEmbroideryType("");
    setPatternRepeat("");
    setPatternFollowUp("");
    setPatternFollowUpText("");
    setPatternOffset("");
    setPatternDensity("");
    setPatternScale("");
    setPatternColorCount("");
    setPatternProcess("");
    setInfluence("");
    setSelectedMacroThemeId("");
    setSelectedColourStoryId("");
    setSelectedPatternTrendId("");
    setSelectedPaletteIndex(-1);
    setSelectedPreferences([]);
    setPreferencePage(1);
  }, [market, category, seasonId, sessionLocked]);

  const currentMacro = useMemo(
    () =>
      catalog?.macros.find(macro => macro.id === selectedMacroThemeId) ||
      catalog?.macros[0],
    [catalog, selectedMacroThemeId]
  );

  const colourStories = currentMacro?.colourStories || [];
  const patternTrends = currentMacro?.patternTrends || [];
  const currentTrend =
    patternTrends.find(trend => trend.id === selectedPatternTrendId) ||
    patternTrends[0];
  const colourReportSlides = useMemo(
    () =>
      selectedMacroThemeId && currentMacro?.id === selectedMacroThemeId
        ? reportSlidesForMacroThemeColour(currentMacro)
        : [],
    [currentMacro, selectedMacroThemeId]
  );
  const microReportSlides = useMemo(
    () =>
      reportSlidesForItem(
        patternTrends.find(trend => trend.id === selectedPatternTrendId) || null
      ),
    [patternTrends, selectedPatternTrendId]
  );
  const preferenceAssets = currentTrend?.assets || [];
  const preferencePageCount = Math.max(
    1,
    Math.ceil(preferenceAssets.length / PREFERENCE_PAGE_SIZE)
  );
  const pagedPreferenceAssets = preferenceAssets.slice(
    (preferencePage - 1) * PREFERENCE_PAGE_SIZE,
    preferencePage * PREFERENCE_PAGE_SIZE
  );

  const paletteStories = isDenimPath ? denimColourStories : colourStories;
  const selectedPalette =
    selectedPaletteIndex >= 0 && selectedPaletteIndex < paletteStories.length
      ? paletteStories[selectedPaletteIndex]
      : null;
  const customPalette =
    selectedPaletteIndex >= paletteStories.length
      ? customPalettes[selectedPaletteIndex - paletteStories.length]
      : null;
  const hasPatternSelected = selectedPreferences.length > 0;

  const patternTrendKey = patternTrends.map(trend => trend.id).join("|");
  const suggestedPreferenceStem = selectedPreferences[0] || "";
  const suggestedPreferenceAsset = useMemo(() => {
    if (patternChoice !== "recommend" || !suggestedPreferenceStem) {
      return null;
    }
    const inCurrent = (currentTrend?.assets || []).find(
      asset => asset.stem === suggestedPreferenceStem
    );
    if (inCurrent) {
      return inCurrent;
    }
    for (const trend of patternTrends) {
      const matched = (trend.assets || []).find(
        asset => asset.stem === suggestedPreferenceStem
      );
      if (matched) {
        return matched;
      }
    }
    return null;
  }, [
    patternChoice,
    suggestedPreferenceStem,
    currentTrend?.id,
    patternTrendKey,
  ]);

  useEffect(() => {
    if (sessionLocked || patternChoice !== "choose") {
      return;
    }
    setPreferencePage(1);
    setSelectedPreferences([]);
  }, [selectedPatternTrendId, sessionLocked, patternChoice]);

  useEffect(() => {
    if (sessionLocked || isDenimPath || patternChoice !== "recommend") {
      return;
    }
    if (!patternTrends.length) {
      return;
    }
    // Randomness: Math.random() picks one micro-theme among available
    // patternTrends, then Math.random() picks one preference asset within
    // that theme (preferring assets marked available).
    const trend =
      patternTrends[Math.floor(Math.random() * patternTrends.length)];
    const available = (trend.assets || []).filter(asset => asset.available);
    const pool = available.length ? available : trend.assets || [];
    const asset =
      pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : null;
    setSelectedPatternTrendId(trend.id);
    setSelectedPreferences(asset ? [asset.stem] : []);
    setPreferencePage(1);
    // patternTrends read via patternTrendKey; length/key changes re-roll suggestion.
  }, [patternChoice, patternTrendKey, sessionLocked, isDenimPath]);

  useEffect(() => {
    if (sessionLocked || isDenimPath || patternChoice === "recommend") {
      return;
    }
    setSelectedPreferences([]);
    setSelectedPatternTrendId("");
    if (patternChoice === "choose") {
      setPreferencePage(1);
    }
  }, [patternChoice, sessionLocked, isDenimPath]);

  useEffect(() => {
    if (sessionLocked || isDenimPath || trendSource === "surface") {
      return;
    }
    if (!patternTrends.length) {
      if (patternChoice !== "none") {
        setPatternChoice("none");
      }
      return;
    }
    if (patternChoice === "none") {
      setPatternChoice("");
    }
  }, [
    patternTrends.length,
    isDenimPath,
    sessionLocked,
    patternChoice,
    trendSource,
  ]);

  useEffect(() => {
    let cancelled = false;
    const pageAssets = (currentTrend?.assets || []).slice(
      (preferencePage - 1) * PREFERENCE_PAGE_SIZE,
      preferencePage * PREFERENCE_PAGE_SIZE
    );

    (async () => {
      const missing = pageAssets.filter(
        asset => asset.available && !assetPreviewCache.current.has(asset.stem)
      );
      if (!missing.length) {
        return;
      }
      const updates: Record<string, string> = {};
      await Promise.all(
        missing.map(async asset => {
          try {
            // HTTPS CDN urls resolve synchronously; API paths still blob-fetch.
            const previewUrl = await getImageGeneratorAssetPreview(asset.url);
            assetPreviewCache.current.set(asset.stem, previewUrl);
            updates[asset.stem] = previewUrl;
          } catch {
            // Keep the tile as a placeholder if the asset cannot be fetched.
          }
        })
      );
      if (!cancelled && Object.keys(updates).length) {
        setAssetPreviews(prev => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentTrend?.id, preferencePage]);

  useEffect(() => {
    if (
      patternChoice !== "recommend" ||
      !suggestedPreferenceAsset?.available ||
      !suggestedPreferenceAsset.url
    ) {
      return;
    }
    if (assetPreviewCache.current.has(suggestedPreferenceAsset.stem)) {
      const previewUrl = assetPreviewCache.current.get(
        suggestedPreferenceAsset.stem
      );
      if (previewUrl) {
        setAssetPreviews(prev =>
          prev[suggestedPreferenceAsset.stem]
            ? prev
            : { ...prev, [suggestedPreferenceAsset.stem]: previewUrl }
        );
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const previewUrl = await getImageGeneratorAssetPreview(
          suggestedPreferenceAsset.url
        );
        assetPreviewCache.current.set(
          suggestedPreferenceAsset.stem,
          previewUrl
        );
        if (!cancelled) {
          setAssetPreviews(prev => ({
            ...prev,
            [suggestedPreferenceAsset.stem]: previewUrl,
          }));
        }
      } catch {
        // Keep placeholder when preview fetch fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    patternChoice,
    suggestedPreferenceAsset?.stem,
    suggestedPreferenceAsset?.url,
  ]);

  useEffect(
    () => () => {
      assetPreviewCache.current.forEach(url => URL.revokeObjectURL(url));
      assetPreviewCache.current.clear();
    },
    []
  );

  const applyMacroTheme = (macroId: string) => {
    setSelectedMacroThemeId(macroId);
    setSelectedPaletteIndex(-1);
    setSelectedColourStoryId("");
    setSelectedPatternTrendId("");
    setSelectedPreferences([]);
    setPreferencePage(1);
    setTrendSource("");
    setSelectedSurfaceId("");
    setSurfacePage(1);
    setShowColourReport(false);
    setShowMicroReport(false);
  };

  const clearMacroBranch = () => {
    setSelectedMacroThemeId("");
    setSelectedColourStoryId("");
    setSelectedPatternTrendId("");
    setSelectedPreferences([]);
    setPatternChoice("");
    setTrendSource("");
    setSelectedSurfaceId("");
    setSurfacePage(1);
    setSelectedPaletteIndex(-1);
    setCustomPalettes([]);
    setMakeOwnOpen(false);
  };

  const clearDenimBranch = () => {
    setDenimMaterialId("");
    setDenimWashId("");
    setSelectedPaletteIndex(-1);
    setCustomPalettes([]);
    setSelectedColourStoryId("");
    setMakeOwnOpen(false);
  };

  const applyStartingDirection = (
    next: ImageGeneratorStartingDirection | ""
  ) => {
    const resolved = next === "denim" && !denimAllowed ? "macro-theme" : next;
    if (resolved !== "denim") {
      clearDenimBranch();
    }
    if (resolved !== "macro-theme") {
      clearMacroBranch();
    }
    setStartingDirection(resolved);
  };

  const applySessionRecord = (record: ImageGeneratorHistoryRecord) => {
    const payload = (record.requestPayload ||
      {}) as Partial<ImageGeneratorGenerateStylePayload>;
    const details = record.details;
    setOutputType(
      record.outputType === "style" || details?.outputType === "Style"
        ? "Style"
        : "Pattern"
    );
    const payloadSeasonLabel = payload.season || record.season || "";
    const resolvedSeasonId =
      payload.seasonId ||
      seasons.find(item => item.label === payloadSeasonLabel)?.id ||
      seasons.find(item => item.id === payloadSeasonLabel)?.id ||
      "";
    setSeasonId(resolvedSeasonId);
    pendingSessionRef.current = {
      seasonId: resolvedSeasonId || payload.seasonId,
      seasonLabel: payloadSeasonLabel,
      macroThemeId: payload.macroThemeId,
      colourStoryId:
        (payload.startingDirection || details?.startingDirection) === "denim" ||
        payload.skipReportThemes
          ? undefined
          : payload.colourStoryId,
      patternTrendId: payload.patternTrendId,
    };
    setMarket(payload.market || details?.market || "");
    const hydratedCategory =
      payload.category || details?.category || category || "";
    setCategory(hydratedCategory);
    setSelectedMacroThemeId(payload.macroThemeId || "");
    setSelectedPatternTrendId(payload.patternTrendId || "");
    const hydratedSurfaceId = (
      payload.surfaceId ||
      details?.surfaceId ||
      ""
    ).trim();
    if (hydratedSurfaceId) {
      setTrendSource("surface");
      setSelectedSurfaceId(hydratedSurfaceId);
      setPatternChoice("none");
      setSelectedPreferences([]);
    } else {
      setTrendSource("pattern");
      setSelectedSurfaceId("");
      setSurfacePage(1);
      setPatternChoice(
        (payload.patternChoice || details?.patternChoice || "choose") as
          | ImageGeneratorPatternChoice
          | ""
      );
      setSelectedPreferences(
        payload.selectedAssetStems?.length
          ? payload.selectedAssetStems
          : (details?.preferences || []).map(item => item.stem)
      );
    }
    const storedFormatRaw =
      payload.garmentFormat || details?.garmentFormat || "";
    const storedFormat =
      storedFormatRaw === "Jacquard" ? "Print" : storedFormatRaw;
    setGarmentFormat(storedFormat);
    setSelectedEmbroideryType(
      payload.embroideryType || details?.embroideryType || ""
    );
    const storedRepeatRaw =
      payload.patternRepeat ||
      details?.patternRepeat ||
      (storedFormat === "Graphic"
        ? "Placement, Engineered"
        : storedFormat
          ? "AOP, Engineered, Placement, Panel, Border"
          : "");
    const storedRepeat =
      storedRepeatRaw === "Borders"
        ? "Border"
        : storedRepeatRaw.replace(/\bBorders\b/g, "Border");
    setPatternRepeat(storedRepeat);

    const storedFollowUp =
      payload.patternFollowUp || details?.patternFollowUp || "";
    const storedFollowUpText =
      payload.patternFollowUpText || details?.patternFollowUpText || "";
    const firstRepeat = storedRepeat.split(",")[0]?.trim();
    const encodeLegacy = (raw: string) => {
      if (!raw || raw.includes(":") || !firstRepeat) {
        return raw;
      }
      return `${firstRepeat}: ${raw}`;
    };
    setPatternFollowUp(encodeLegacy(storedFollowUp));
    setPatternFollowUpText(encodeLegacy(storedFollowUpText));
    setPatternOffset(payload.patternOffset || details?.patternOffset || "");
    setPatternDensity(payload.patternDensity || details?.patternDensity || "");
    setPatternScale(payload.patternScale || details?.patternScale || "");
    setPatternColorCount(
      payload.patternColorCount || details?.patternColorCount || ""
    );
    setPatternProcess(payload.patternProcess || details?.patternProcess || "");
    setProductType(payload.productType || details?.productType || productType);
    const hydratedInfluence = payload.influence || details?.influence || "";
    const hydratedSpecificationUrl = String(
      payload.specificationImageUrl || ""
    ).trim();
    // Pre–Upload-influence looks stored a spec URL under Library; map to Upload.
    setInfluence(
      hydratedSpecificationUrl &&
        !isForecastInfluence(hydratedInfluence) &&
        !isUploadInfluence(hydratedInfluence)
        ? UPLOAD_INFLUENCE
        : hydratedInfluence
    );
    setStyleType(payload.styleType || details?.styleType || styleType);
    setMaterial(payload.material || details?.material || material);
    setSleeve(payload.sleeve || details?.sleeve || sleeve);
    setNeckline(payload.neckline || details?.neckline || neckline);
    setLength(payload.length || details?.length || "");
    setFit(payload.fit || details?.fit || fit);
    setWash(payload.wash || details?.wash || wash);
    setForecastSilhouetteId(payload.forecastSilhouetteId || "");
    setForecastDetailId(payload.forecastDetailId || "");
    setSpecificationImageUrl(hydratedSpecificationUrl);
    setSpecificationActive(Boolean(hydratedSpecificationUrl));
    setSpecificationUploading(false);
    const resolvedProduct =
      payload.productType || details?.productType || productType;
    const canHydrateDenim = isDenimEligibleCategory(hydratedCategory);
    let hydratedDirection =
      payload.startingDirection ||
      details?.startingDirection ||
      (payload.skipReportThemes &&
      isDenimEligibleProductType(resolvedProduct, hydratedCategory)
        ? "denim"
        : "");
    if (hydratedDirection === "denim" && !canHydrateDenim) {
      hydratedDirection = "macro-theme";
    }
    setStartingDirection(
      hydratedDirection === "denim" || hydratedDirection === "macro-theme"
        ? hydratedDirection
        : ""
    );
    const denimHydrated = hydratedDirection === "denim" && canHydrateDenim;
    setDenimMaterialId(
      denimHydrated
        ? payload.denimMaterialId || details?.denimMaterialId || ""
        : ""
    );
    setDenimWashId(
      denimHydrated ? payload.denimWashId || details?.denimWashId || "" : ""
    );
    setAdditionalPrompt(payload.additionalPrompt || "");

    const macro =
      catalog?.macros.find(item => item.id === payload.macroThemeId) ||
      currentMacro;
    const stories = denimHydrated
      ? denimColourStories
      : macro?.colourStories || [];
    const colourId = payload.colourStoryId || "";
    const storyIndex = stories.findIndex(story => story.id === colourId);
    const paletteHexes = payload.paletteHexes || details?.paletteHexes || [];
    if (storyIndex >= 0) {
      setSelectedPaletteIndex(storyIndex);
      setSelectedColourStoryId(stories[storyIndex].id);
    } else if (paletteHexes.length) {
      setCustomPalettes(prev => {
        const exists = prev.some(
          item => item.hexes.join("|") === paletteHexes.join("|")
        );
        return exists
          ? prev
          : [...prev, { hexes: paletteHexes, title: "Custom palette" }];
      });
      setSelectedPaletteIndex(stories.length);
      setSelectedColourStoryId("");
    }
  };

  const startNewGeneration = () => {
    hydratedSessionId.current = null;
    setActiveTab("query");
    setSearchParams({}, { replace: true });
    setResultItems([]);
    setSelectedResultId("");
    setOutputType("");
    setMarket("");
    setCategory("");
    setSeasonId("");
    setSeasons([]);
    setCatalog(null);
    pendingSessionRef.current = null;
    setSelectedMacroThemeId("");
    setSelectedColourStoryId("");
    setSelectedPatternTrendId("");
    setSelectedPaletteIndex(-1);
    setCustomPalettes([]);
    setMakeOwnOpen(false);
    setMakeOwnSlots(emptyMakeOwnSlots());
    setMakeOwnSlotIndex(0);
    setMakeOwnEditingIndex(null);
    setPatternChoice("");
    setTrendSource("");
    setSelectedSurfaceId("");
    setSurfacePage(1);
    setSurfaceSlides([]);
    setSelectedPreferences([]);
    setPreferencePage(1);
    setGarmentFormat("");
    setSelectedEmbroideryType("");
    setPatternRepeat("");
    setPatternFollowUp("");
    setPatternFollowUpText("");
    setPatternOffset("");
    setPatternDensity("");
    setPatternScale("");
    setPatternColorCount("");
    setPatternProcess("");
    setInfluence("");
    setProductType("");
    setStyleType("");
    setMaterial("");
    setSleeve("");
    setNeckline("");
    setLength("");
    setFit("");
    setWash("");
    setForecastSilhouetteId("");
    setForecastDetailId("");
    setForecastSketchOpen(false);
    setSpecificationImageUrl("");
    setSpecificationActive(false);
    setSpecificationUploading(false);
    setSpecificationResetKey(key => key + 1);
    setStartingDirection("");
    setDenimMaterialId("");
    setDenimWashId("");
    setAdditionalPrompt("");
  };

  useEffect(() => {
    if (!activeSessionId) {
      hydratedSessionId.current = null;
      missingSessionId.current = null;
      loadingSessionId.current = null;
      return;
    }
    if (missingSessionId.current === activeSessionId) {
      return;
    }
    const byId = new Map(sessions.map(item => [item.id, item]));
    const opened = byId.get(activeSessionId);
    const rootId = opened
      ? imageGeneratorRootId(opened, byId)
      : activeSessionId;
    const chain = imageGeneratorSessionChain(sessions, rootId);
    if (!chain.length) {
      if (loadingSessionId.current === activeSessionId) {
        return;
      }
      loadingSessionId.current = activeSessionId;
      void loadSession(activeSessionId)
        .then(record => {
          if (record) {
            return;
          }
          missingSessionId.current = activeSessionId;
          setSearchParams(
            prev => {
              const next = new URLSearchParams(prev);
              next.delete(IMAGE_GENERATOR_SESSION_PARAM);
              return next;
            },
            { replace: true }
          );
        })
        .finally(() => {
          if (loadingSessionId.current === activeSessionId) {
            loadingSessionId.current = null;
          }
        });
      return;
    }
    loadingSessionId.current = null;
    const assetUrlByStem: Record<string, string> = {};
    (catalog?.macros || []).forEach(macro => {
      (macro.patternTrends || []).forEach(trend => {
        (trend.assets || []).forEach(asset => {
          if (asset.stem && asset.url) {
            assetUrlByStem[asset.stem] = asset.url;
          }
        });
      });
    });
    [...denimTiles].forEach(tile => {
      if (!tile.url) {
        return;
      }
      assetUrlByStem[tile.id] = tile.url;
      assetUrlByStem[tile.url] = tile.url;
      // Backend may store preference stems as filenames, e.g. material-10-oz-....png
      const fileStem = `${tile.kind === "materials" ? "material" : "wash"}-${tile.id}`;
      assetUrlByStem[fileStem] = tile.url;
      assetUrlByStem[`${fileStem}.png`] = tile.url;
      assetUrlByStem[`${fileStem}.jpg`] = tile.url;
      assetUrlByStem[`${fileStem}.jpeg`] = tile.url;
      assetUrlByStem[`${fileStem}.webp`] = tile.url;
    });
    // Seed from stored history so preference tiles still resolve when the
    // live catalog no longer includes that macro's assets.
    chain.forEach(record => {
      (record.details?.preferences || []).forEach(pref => {
        if (pref.stem && pref.url) {
          assetUrlByStem[pref.stem] = pref.url;
        }
      });
      (record.references?.assets || []).forEach(asset => {
        const url = (asset.url || asset.relativeKey || "").trim();
        if (asset.stem && url) {
          assetUrlByStem[asset.stem] = url;
        }
      });
    });
    const surfaceCatalogById: Record<
      string,
      { name: string; reportTitle: string }
    > = {};
    surfaceSlides.forEach(slide => {
      const id = String(slide.id || "").trim();
      if (!id) {
        return;
      }
      surfaceCatalogById[id] = {
        name: slide.name || "",
        reportTitle: slide.reportTitle || "",
      };
    });
    const items = chain
      .map(record =>
        mapImageGeneratorHistoryRecord(
          record,
          undefined,
          assetUrlByStem,
          surfaceCatalogById
        )
      )
      .filter((item): item is ImageGeneratorResultItem => Boolean(item));
    const preferenceSignature = (item: ImageGeneratorResultItem) =>
      (item.details.preferences || [])
        .map(pref => `${pref.stem}:${pref.url || ""}`)
        .join("|");
    const reviewSignature = (item: ImageGeneratorResultItem) => {
      const review = item.result.review || {};
      const likes = review.likes || {};
      const library = review.library;
      return [
        review.approvedAt || "",
        library?.fileName || "",
        library?.name || "",
        library?.savedAt || "",
        likes.front === true ? "1" : likes.front === false ? "0" : "",
        likes.back === true ? "1" : likes.back === false ? "0" : "",
        String(review.feedback?.length || 0),
      ].join("|");
    };
    setResultItems(prev => {
      if (
        prev.length === items.length &&
        prev.every(
          (item, index) =>
            item.id === items[index]?.id &&
            item.result.status === items[index]?.result.status &&
            item.result.imageUrl === items[index]?.result.imageUrl &&
            item.result.backImageUrl === items[index]?.result.backImageUrl &&
            item.details.designSheet?.status ===
              items[index]?.details.designSheet?.status &&
            (item.details.microTheme || "") ===
              (items[index]?.details.microTheme || "") &&
            (item.details.surfaceName || "") ===
              (items[index]?.details.surfaceName || "") &&
            preferenceSignature(item) ===
              preferenceSignature(items[index] as ImageGeneratorResultItem) &&
            reviewSignature(item) ===
              reviewSignature(items[index] as ImageGeneratorResultItem)
        )
      ) {
        return prev;
      }
      return items;
    });
    const preferredId = String(activeSessionId);
    if (hydratedSessionId.current !== rootId) {
      const root =
        chain.find(item => item.id === rootId) || chain[chain.length - 1];
      applySessionRecord(root);
      hydratedSessionId.current = rootId;
      setSelectedResultId(
        items.some(item => item.id === preferredId)
          ? preferredId
          : items[0]?.id || ""
      );
      setResultScrollKey(value => value + 1);
      return;
    }
    setSelectedResultId(prev =>
      items.some(item => item.id === prev) ? prev : items[0]?.id || ""
    );
  }, [
    activeSessionId,
    catalog,
    denimTiles,
    loadSession,
    sessions,
    setSearchParams,
    surfaceSlides,
  ]);

  const clearPalette = () => {
    setSelectedPaletteIndex(-1);
    setSelectedColourStoryId("");
  };

  const togglePreference = (stem: string) => {
    if (patternChoice !== "choose") {
      return;
    }
    if (selectedPreferences[0] === stem) {
      setSelectedPreferences([]);
      return;
    }
    setSelectedPreferences([stem]);
  };

  const addCustomPalette = (
    hexes: string[],
    title: string,
    swatches?: CustomPalette["swatches"]
  ) => {
    if (!hexes.length) {
      return;
    }
    setCustomPalettes(prev => {
      const nextIndex = prev.length;
      setSelectedPaletteIndex(paletteStories.length + nextIndex);
      setMakeOwnEditingIndex(nextIndex);
      return [...prev, { hexes, title, swatches }];
    });
    setSelectedColourStoryId("");
  };

  const commitMakeOwnPalette = (slots: Array<string | null>) => {
    const hexes = slots.filter((hex): hex is string => Boolean(hex));
    if (!hexes.length) {
      return;
    }
    if (makeOwnEditingIndex != null) {
      setCustomPalettes(prev =>
        prev.map((item, index) =>
          index === makeOwnEditingIndex ? { ...item, hexes } : item
        )
      );
      setSelectedPaletteIndex(paletteStories.length + makeOwnEditingIndex);
      setSelectedColourStoryId("");
      return;
    }
    addCustomPalette(hexes, "Custom palette");
  };

  const openMakeOwnPicker = () => {
    if (makeOwnOpen) {
      setMakeOwnOpen(false);
      return;
    }
    const customIndex = selectedPaletteIndex - paletteStories.length;
    if (customIndex >= 0 && customPalettes[customIndex]) {
      openEditCustomPalette(customIndex);
      return;
    }
    setMakeOwnSlots(emptyMakeOwnSlots());
    setMakeOwnSlotIndex(0);
    setMakeOwnEditingIndex(null);
    setMakeOwnOpen(true);
  };

  const openEditCustomPalette = (customIndex: number) => {
    const editing = customPalettes[customIndex];
    if (!editing) {
      return;
    }
    const nextSlots = emptyMakeOwnSlots(editing.hexes);
    setMakeOwnSlots(nextSlots);
    setMakeOwnSlotIndex(0);
    setMakeOwnEditingIndex(customIndex);
    setSelectedPaletteIndex(paletteStories.length + customIndex);
    setSelectedColourStoryId("");
    setMakeOwnOpen(true);
  };

  const handleMakeOwnAdd = (hex: string) => {
    const next = [...makeOwnSlots];
    const emptyIndex = next.findIndex(item => !item);
    let index =
      !next[makeOwnSlotIndex] && makeOwnSlotIndex >= 0
        ? makeOwnSlotIndex
        : emptyIndex;
    if (index < 0) {
      if (next.length >= MAKE_OWN_MAX_SLOTS) {
        index = makeOwnSlotIndex;
      } else {
        next.push(null);
        index = next.length - 1;
      }
    }
    next[index] = hex;
    setMakeOwnSlots(next);
    const followingEmpty = next.findIndex(
      (item, slotIndex) => slotIndex > index && !item
    );
    setMakeOwnSlotIndex(
      followingEmpty >= 0 ? followingEmpty : Math.min(index, next.length - 1)
    );
    commitMakeOwnPalette(next);
  };

  const handleAddMakeOwnSlot = () => {
    if (makeOwnSlots.length >= MAKE_OWN_MAX_SLOTS) {
      return;
    }
    const next = [...makeOwnSlots, null];
    setMakeOwnSlots(next);
    setMakeOwnSlotIndex(next.length - 1);
  };

  const handleRemoveMakeOwnSlot = (index: number) => {
    if (index < 0 || index >= makeOwnSlots.length) {
      return;
    }
    const next = [...makeOwnSlots];
    if (next.length > MAKE_OWN_DEFAULT_SLOTS) {
      next.splice(index, 1);
    } else {
      next[index] = null;
    }
    // Keep at least the default number of empty slots.
    while (next.length < MAKE_OWN_DEFAULT_SLOTS) {
      next.push(null);
    }
    setMakeOwnSlots(next);
    const nextSelected = Math.min(
      makeOwnSlotIndex >= next.length ? next.length - 1 : makeOwnSlotIndex,
      Math.max(0, next.length - 1)
    );
    setMakeOwnSlotIndex(nextSelected);
    commitMakeOwnPalette(next);
  };

  const handleMakeOwnSlotChange = (index: number, hex: string) => {
    const next = [...makeOwnSlots];
    next[index] = hex;
    setMakeOwnSlots(next);
    commitMakeOwnPalette(next);
  };

  const handleUploadPalette = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const fileError = paletteExtractFileError(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }
    setPaletteExtracting(true);
    try {
      const mapped = mapExtractedPalette(await extractGenerativePalette(file));
      if (!mapped.hexes.length) {
        toast.error("No fashion colours were found in that image.");
        return;
      }
      addCustomPalette(
        mapped.hexes,
        mapped.title,
        mapped.swatches.map(item => ({
          hex: item.hex,
          name: item.name,
          tier: item.tier,
        }))
      );
      toast.success("Palette extracted from image.");
    } catch (error) {
      toast.error(
        getImageGeneratorErrorMessage(
          error,
          "Unable to extract a palette from that image."
        )
      );
    } finally {
      setPaletteExtracting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        makeOwnRef.current &&
        !makeOwnRef.current.contains(event.target as Node)
      ) {
        setMakeOwnOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isDenimPath || !market || !category || !seasonLabel) {
      setSurfaceSlides([]);
      setSurfacesLoading(false);
      return;
    }
    let cancelled = false;
    setSurfacesLoading(true);
    getImageGeneratorSurfacesCatalog({
      season: seasonLabel,
      seasonId,
      market,
      category,
      genre,
    })
      .then(next => {
        if (cancelled) {
          return;
        }
        const slides = next?.surfaces || [];
        setSurfaceSlides(slides);
        if (sessionLocked && selectedSurfaceId) {
          const index = slides.findIndex(
            slide => slide.id === selectedSurfaceId
          );
          if (index >= 0) {
            setSurfacePage(index + 1);
            return;
          }
        }
        if (!sessionLocked) {
          const keep =
            selectedSurfaceId &&
            slides.some(slide => slide.id === selectedSurfaceId);
          if (!keep) {
            setSelectedSurfaceId("");
            setSurfacePage(1);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSurfaceSlides([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSurfacesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // selectedSurfaceId intentionally omitted — hydrate/page sync handled inside.
  }, [
    isDenimPath,
    market,
    category,
    seasonLabel,
    seasonId,
    genre,
    sessionLocked,
  ]);

  const forecastFilterKey = `${influence}|${seasonLabel}|${market}|${category}`;
  const forecastFilterKeyRef = useRef(forecastFilterKey);
  const forecastReportsApplicable =
    outputType === "Style" &&
    isForecastInfluence(influence) &&
    Boolean(seasonLabel && market && category);
  const showForecastReports = forecastReportsApplicable;

  useEffect(() => {
    if (!showForecastReports || sessionLocked) {
      setForecastSketchOpen(false);
      return;
    }
    if (forecastSketchAutoOpenToken === forecastSketchAutoOpenSeen) {
      return;
    }
    setForecastSketchOpen(true);
    setForecastSketchAutoOpenSeen(forecastSketchAutoOpenToken);
  }, [
    showForecastReports,
    sessionLocked,
    forecastSketchAutoOpenToken,
    forecastSketchAutoOpenSeen,
  ]);

  useEffect(() => {
    const keyChanged = forecastFilterKeyRef.current !== forecastFilterKey;
    if (keyChanged) {
      forecastFilterKeyRef.current = forecastFilterKey;
    }
    const dropSelection = keyChanged && !sessionLocked;
    if (!forecastReportsApplicable) {
      setForecastSilhouetteSlides([]);
      setForecastDetailSlides([]);
      setForecastReportsLoading(false);
      if (dropSelection) {
        setForecastSilhouetteId("");
        setForecastDetailId("");
      }
      return;
    }

    let cancelled = false;
    setForecastReportsLoading(true);
    getImageGeneratorForecastReports({
      season: seasonLabel,
      seasonId,
      market,
      category,
      genre,
    })
      .then(next => {
        if (cancelled) {
          return;
        }
        const silhouette = next?.silhouette || [];
        const detailed = next?.detailed || [];
        setForecastSilhouetteSlides(silhouette);
        setForecastDetailSlides(detailed);
        setForecastSilhouetteId(prev => {
          const current = dropSelection ? "" : prev;
          if (current && silhouette.some(slide => slide.id === current)) {
            return current;
          }
          return "";
        });
        setForecastDetailId(prev => {
          const current = dropSelection ? "" : prev;
          if (current && detailed.some(slide => slide.id === current)) {
            return current;
          }
          return "";
        });
      })
      .catch(() => {
        if (!cancelled) {
          setForecastSilhouetteSlides([]);
          setForecastDetailSlides([]);
          if (!sessionLocked) {
            setForecastSilhouetteId("");
            setForecastDetailId("");
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setForecastReportsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    forecastReportsApplicable,
    forecastFilterKey,
    seasonLabel,
    seasonId,
    market,
    category,
    genre,
    sessionLocked,
    influence,
    outputType,
  ]);

  const applyTrendSource = (next: "pattern" | "surface" | "") => {
    if (sessionLocked) {
      return;
    }
    setTrendSource(next);
    if (next === "surface") {
      setPatternChoice("none");
      setSelectedPatternTrendId("");
      setSelectedPreferences([]);
      setPreferencePage(1);
      setSelectedSurfaceId("");
      return;
    }
    if (next === "pattern") {
      setSelectedSurfaceId("");
      setPatternChoice(patternTrends.length ? "" : "none");
    }
  };

  const buildBasePayload = () => {
    const isSurfacePath =
      trendSource === "surface" && Boolean(selectedSurfaceId);
    const resolvedChoice = isSurfacePath
      ? "none"
      : patternChoice || (patternTrends.length ? "choose" : "none");
    const skipPatternTrend =
      isSurfacePath ||
      resolvedChoice === "none" ||
      (outputType === "Pattern" && resolvedChoice === "recommend");
    const reportColourStoryId =
      selectedPalette?.id || selectedColourStoryId || "";
    const omitStyleRenderFormat =
      outputType === "Style" && trendSource === "surface";
    return buildImageGeneratorBasePayload({
      season: seasonLabel,
      seasonId,
      genre,
      market,
      category,
      macroThemeId: selectedMacroThemeId,
      // Custom / uploaded palettes rely on paletteHexes; omit report id.
      colourStoryId: customPalette ? "" : reportColourStoryId,
      patternTrendId: skipPatternTrend
        ? ""
        : currentTrend?.id || selectedPatternTrendId,
      patternChoice: resolvedChoice,
      selectedAssetStems:
        skipPatternTrend || selectedPreferences.length === 0
          ? []
          : [selectedPreferences[0]],
      ...(isSurfacePath ? { surfaceId: selectedSurfaceId } : {}),
      paletteHexes:
        customPalette?.hexes || selectedPalette?.palette.map(item => item.hex),
      garmentFormat: omitStyleRenderFormat ? undefined : garmentFormat,
      embroideryType: omitStyleRenderFormat
        ? undefined
        : garmentFormat === "Embroidery"
          ? selectedEmbroideryType
          : undefined,
      patternProcess: omitStyleRenderFormat
        ? undefined
        : garmentFormat === "Print"
          ? patternProcess || undefined
          : undefined,
      additionalPrompt,
      ...(outputType === "Pattern"
        ? {
            patternRepeat,
            patternFollowUp: patternFollowUp || undefined,
            patternFollowUpText: patternFollowUpText.trim() || undefined,
            patternOffset: patternOffset || undefined,
            patternDensity,
            patternScale,
            patternColorCount,
          }
        : {}),
    });
  };

  const handleGenerate = async () => {
    if (busy) {
      return;
    }
    if (!market || !category) {
      toast.error("Select market and category first.");
      return;
    }
    if (!outputType) {
      toast.error("Select style or pattern.");
      return;
    }
    if (!catalog || !seasonLabel) {
      toast.error("Select market, category, and season first.");
      return;
    }
    if (outputType === "Style" && !startingDirection) {
      toast.error("Select a starting direction.");
      return;
    }
    if (isDenimPath && !selectedPalette && !customPalette) {
      toast.error("Select a colour palette.");
      return;
    }
    if (isDenimPath && !denimMaterialId) {
      toast.error("Select a denim material.");
      return;
    }
    if (isDenimPath && !denimWashId) {
      toast.error("Select a denim wash.");
      return;
    }
    if (!isDenimPath && !hasReportThemes) {
      toast.error("No extracted reports for these filters.");
      return;
    }
    if (
      !isDenimPath &&
      (!selectedMacroThemeId || (!selectedPalette && !customPalette))
    ) {
      toast.error("Select a macro theme and colour palette.");
      return;
    }
    if (!isDenimPath && !trendSource) {
      toast.error("Select Pattern or Surface Trend.");
      return;
    }
    if (!isDenimPath && trendSource === "surface" && !selectedSurfaceId) {
      toast.error("Select a slide report.");
      return;
    }
    if (
      !isDenimPath &&
      trendSource === "pattern" &&
      !patternChoice &&
      patternTrends.length > 0
    ) {
      toast.error("Select how you want the pattern chosen.");
      return;
    }
    if (
      !isDenimPath &&
      trendSource === "pattern" &&
      patternChoice === "choose" &&
      patternTrends.length > 0 &&
      selectedPreferences.length !== 1
    ) {
      toast.error("Select one pattern.");
      return;
    }
    // Style Print/Embroidery design sheets need a pattern tile or surface slide.
    // Allowing "none" here creates a look that fails later on Design Sheet.
    if (
      !isDenimPath &&
      outputType === "Style" &&
      (garmentFormat === "Print" || garmentFormat === "Embroidery") &&
      trendSource === "pattern" &&
      (patternChoice === "none" || patternTrends.length === 0)
    ) {
      toast.error(
        "Select a pattern tile, or switch to Surface Trend — needed for the design sheet."
      );
      return;
    }
    const styleSurfaceOmitsRenderFormat =
      outputType === "Style" && trendSource === "surface";
    if (
      !isDenimPath &&
      outputType === "Style" &&
      !styleSurfaceOmitsRenderFormat &&
      !garmentFormat
    ) {
      toast.error("Select a garment format.");
      return;
    }
    if (!isDenimPath && outputType === "Pattern" && !garmentFormat) {
      toast.error("Select a pattern format.");
      return;
    }
    if (
      !isDenimPath &&
      !styleSurfaceOmitsRenderFormat &&
      garmentFormat === "Print" &&
      !patternProcess
    ) {
      toast.error("Select a print format.");
      return;
    }
    if (
      !isDenimPath &&
      outputType === "Pattern" &&
      (!patternDensity || !patternScale || !patternColorCount)
    ) {
      toast.error("Select print density, scale, and max color count.");
      return;
    }
    if (
      !isDenimPath &&
      !styleSurfaceOmitsRenderFormat &&
      garmentFormat === "Embroidery" &&
      !selectedEmbroideryType
    ) {
      toast.error("Select an embroidery stitch.");
      return;
    }
    if (
      outputType === "Style" &&
      (specificationUploading ||
        (specificationActive && !specificationImageUrl))
    ) {
      toast.error(SPECIFICATION_IMAGE_PENDING_ERROR);
      return;
    }
    if (outputType === "Style" && (!productType || !influence)) {
      toast.error("Select product type and influence.");
      return;
    }
    // Required look dropdowns (Style Type / Material / Length / … when marked
    // required in Design your look) hard-block like product type + influence.

    if (outputType === "Style" && !specificationImageActive) {
      const products = productOptionsFor(market, category);
      if (!products.includes(productType)) {
        toast.error("Select product type.");
        return;
      }
      if (
        isLibraryInfluence(influence) &&
        styleType &&
        !styleOptionsFor(market, productType, category).includes(styleType)
      ) {
        toast.error("Select style type.");
        return;
      }
      if (isForecastInfluence(influence)) {
        if (forecastReportsLoading) {
          toast.error(FORECAST_REPORT_COPY.loading);
          return;
        }
        if (
          !forecastSketchSelectionSatisfied(
            forecastSilhouetteSlides.length,
            forecastDetailSlides.length,
            forecastSilhouetteId,
            forecastDetailId
          )
        ) {
          toast.error(
            forecastSketchMissingMessage(
              forecastSilhouetteSlides.length,
              forecastDetailSlides.length,
              forecastSilhouetteId,
              forecastDetailId
            )
          );
          return;
        }
      }
    }

    setBusy("generate");
    try {
      const result =
        outputType === "Pattern"
          ? await generateImageGeneratorPattern(buildBasePayload())
          : await generateImageGeneratorStyle({
              ...buildBasePayload(),
              productType,
              influence,
              ...(isForecastInfluence(influence) || isUploadInfluence(influence)
                ? {}
                : { styleType }),
              material: showFollowUp("material") ? material : undefined,
              sleeve: showFollowUp("sleeve") ? sleeve : undefined,
              neckline: showFollowUp("neckline") ? neckline : undefined,
              length: showFollowUp("length") ? length : undefined,
              fit: showFollowUp("fit") ? fit : undefined,
              wash: showFollowUp("wash") ? wash : undefined,
              startingDirection: startingDirection || undefined,
              denimMaterialId: isDenimPath ? denimMaterialId : undefined,
              denimWashId: isDenimPath ? denimWashId : undefined,
              skipReportThemes: isDenimPath,
              ...(specificationImageUrl ? { specificationImageUrl } : {}),
              ...(isForecastInfluence(influence)
                ? {
                    forecastSilhouetteId: forecastSilhouetteId || undefined,
                    forecastDetailId: forecastDetailId || undefined,
                  }
                : {}),
            });

      if (result.id) {
        trackJob(result as ImageGeneratorHistoryRecord);
        setSearchParams(
          { [IMAGE_GENERATOR_SESSION_PARAM]: String(result.id) },
          { replace: true }
        );
      }
      toast.success(
        `${outputType} queued. We’ll notify you in the bell when it’s ready.`
      );
    } catch (error) {
      toast.error(
        getImageGeneratorErrorMessage(
          error,
          `Unable to generate ${outputType.toLowerCase()}.`
        )
      );
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async (
    generationId: string,
    payload: { additionalPrompt: string; views: Array<"front" | "back"> }
  ) => {
    const numericId = Number(generationId);
    if (!Number.isInteger(numericId) || numericId < 1) {
      toast.error("This look is not saved yet. Generate again first.");
      return;
    }
    if (busy) {
      return;
    }
    setBusy("regen");
    try {
      const result = await regenerateImageGenerator(numericId, {
        additionalPrompt: payload.additionalPrompt,
        views: payload.views,
      });
      if (result.id) {
        trackJob(result as ImageGeneratorHistoryRecord);
        setSelectedResultId(String(result.id));
        setSearchParams(
          { [IMAGE_GENERATOR_SESSION_PARAM]: String(result.id) },
          { replace: true }
        );
      }
      toast.success("Queued. We’ll notify you when it’s ready.");
    } catch (error) {
      toast.error(
        getImageGeneratorErrorMessage(error, "Unable to regenerate this look.")
      );
    } finally {
      setBusy(null);
    }
  };

  const renderPreferenceTile = (asset: ImageGeneratorCatalogAsset) => (
    <div key={asset.stem} className="w-full max-w-[165px]">
      <ImageGeneratorPreferenceTile
        asset={asset}
        previewUrl={
          assetPreviews[asset.stem] ||
          (/^https?:\/\//i.test(asset.url) ? asset.url : undefined)
        }
        isSelected={selectedPreferences.includes(asset.stem)}
        choiceLocked={patternChoice !== "choose"}
        isMuted={
          hasPatternSelected && !selectedPreferences.includes(asset.stem)
        }
        onToggle={togglePreference}
        aspectClassName="aspect-[2/3]"
      />
    </div>
  );

  const inputsReady = Boolean(
    market &&
      category &&
      seasonId &&
      seasonLabel &&
      !seasonsLoading &&
      !catalogLoading &&
      catalog
  );
  /**
   * Visible look dropdowns match ImageGeneratorStyleFields. When Influence is
   * Library / Forecast / Upload, those fields show as required (*) — same hard
   * gate as Product Type / Influence before later steps unlock.
   */
  const influenceSelectedForLook =
    isLibraryInfluence(influence) || isMaterialLengthOnlyInfluence(influence);
  // Library only; hidden for Forecast / Upload and when a non-upload spec image is active.
  const visibleStyleTypeDropdown =
    influenceSelectedForLook &&
    isLibraryInfluence(influence) &&
    !specificationImageActive;
  // Spec image on Library keeps Material only; Upload keeps Material + Length.
  const visibleLookFollowUps = !influenceSelectedForLook
    ? []
    : specificationImageActive && !isUploadInfluence(influence)
      ? lookFollowUpFields.filter(field => field === "material")
      : lookFollowUpFields;
  // Required when shown: Style Type (Library) + follow-ups for that influence
  // (Forecast/Upload → Material + Length when the product lists them).
  const styleDropdownsComplete =
    (!visibleStyleTypeDropdown || Boolean(styleType)) &&
    visibleLookFollowUps.every(isFollowUpFilled);
  const hasIncompleteStyleDropdowns =
    outputType === "Style" &&
    influenceSelectedForLook &&
    (visibleStyleTypeDropdown || visibleLookFollowUps.length > 0) &&
    !styleDropdownsComplete;
  // Hard gates: product + influence (+ forecast sketches) + required look fields.
  const styleSectionReady = specificationImageActive
    ? outputType === "Style" &&
      Boolean(productType && influence) &&
      styleDropdownsComplete
    : outputType === "Style" &&
      Boolean(productType && influence) &&
      !isUploadInfluence(influence) &&
      styleDropdownsComplete &&
      (!isForecastInfluence(influence) ||
        !forecastReportsApplicable ||
        (!forecastReportsLoading &&
          forecastSketchSelectionSatisfied(
            forecastSilhouetteSlides.length,
            forecastDetailSlides.length,
            forecastSilhouetteId,
            forecastDetailId
          )));
  const styleReady = styleSectionReady;

  useEffect(() => {
    if (sessionLocked || outputType !== "Style" || denimAllowed) {
      return;
    }
    // Ethnic / non-Western: Starting Direction is hidden (denimAllowed=false),
    // but Macro Theme + later report sections still require macro-theme.
    // Auto-lock so Design your look → Select Macro Theme can open.
    if (startingDirection === "macro-theme") {
      return;
    }
    if (startingDirection === "denim") {
      setDenimMaterialId("");
      setDenimWashId("");
      setSelectedPaletteIndex(-1);
      setCustomPalettes([]);
      setSelectedColourStoryId("");
      setMakeOwnOpen(false);
    }
    setStartingDirection("macro-theme");
  }, [sessionLocked, outputType, denimAllowed, startingDirection]);

  const paletteReady = Boolean(selectedPalette || customPalette);
  /** Full Look (macro) + Print both get Pattern|Surface after palette. Denim skips. */
  const isReportLookPath =
    outputType === "Pattern" ||
    (outputType === "Style" &&
      (startingDirection === "macro-theme" || !denimAllowed));
  const isSurfacePath = trendSource === "surface";
  const isPatternPath = trendSource === "pattern";
  const patternChoicePicked =
    patternChoice === "choose" || patternChoice === "recommend";
  const patternPathReady =
    isDenimPath ||
    (isSurfacePath && Boolean(selectedSurfaceId)) ||
    (isPatternPath &&
      (patternTrends.length === 0 ||
        patternChoice === "none" ||
        patternChoice === "recommend" ||
        (patternChoice === "choose" && hasPatternSelected)));
  const showOutputType = sessionLocked || inputsReady;
  const showStyleFields =
    outputType === "Style" && (sessionLocked || showOutputType);
  // Western only: user picks Macro Theme vs Denim. Ethnic skips this and auto-macro.
  const showStartingDirection =
    outputType === "Style" &&
    denimAllowed &&
    (sessionLocked
      ? Boolean(startingDirection) || styleSectionReady
      : styleSectionReady);
  const startingDirectionOptions = denimAllowed
    ? [...STARTING_DIRECTION_OPTIONS]
    : STARTING_DIRECTION_OPTIONS.filter(
        option => option.value === "macro-theme"
      );
  const showMacro =
    !isDenimPath &&
    (sessionLocked
      ? Boolean(outputType) && startingDirection !== "denim"
      : outputType === "Pattern"
        ? showOutputType
        : styleReady && (startingDirection === "macro-theme" || !denimAllowed));
  const showPalette =
    sessionLocked ||
    (isDenimPath && styleReady) ||
    (!isDenimPath && hasReportThemes && Boolean(selectedMacroThemeId));
  const showDenimMaterials = isDenimPath && (sessionLocked || paletteReady);
  const showDenimWashes =
    isDenimPath &&
    (sessionLocked || (paletteReady && Boolean(denimMaterialId)));
  const showTrendSource =
    isReportLookPath &&
    !isDenimPath &&
    (sessionLocked || (paletteReady && hasReportThemes));
  const showPatternChoice =
    showTrendSource &&
    isPatternPath &&
    patternTrends.length > 0 &&
    (sessionLocked || paletteReady);
  const showMicroTheme =
    showPatternChoice &&
    patternChoice === "choose" &&
    (sessionLocked || patternChoicePicked);
  const showPrefs =
    showMicroTheme && (sessionLocked || Boolean(selectedPatternTrendId));
  const showSlideReports =
    showTrendSource && isSurfacePath && (sessionLocked || paletteReady);
  const showStyleGarment =
    outputType === "Style" &&
    !isDenimPath &&
    !isSurfacePath &&
    (sessionLocked || (paletteReady && patternPathReady));
  const showPatternSpecs =
    outputType === "Pattern" &&
    !isDenimPath &&
    (sessionLocked ||
      (paletteReady &&
        (isSurfacePath
          ? Boolean(selectedSurfaceId)
          : patternChoice === "recommend" ||
            patternChoice === "none" ||
            hasPatternSelected)));
  const patternSpecsReady =
    outputType !== "Pattern" ||
    (Boolean(garmentFormat) &&
      Boolean(patternDensity) &&
      Boolean(patternScale) &&
      Boolean(patternColorCount) &&
      (garmentFormat !== "Print" || Boolean(patternProcess)) &&
      (garmentFormat !== "Embroidery" || Boolean(selectedEmbroideryType)));
  const styleGarmentReady =
    outputType !== "Style" ||
    isDenimPath ||
    isSurfacePath ||
    (Boolean(garmentFormat) &&
      (garmentFormat !== "Print" || Boolean(patternProcess)) &&
      (garmentFormat !== "Embroidery" || Boolean(selectedEmbroideryType)));
  const canGenerate =
    inputsReady &&
    Boolean(outputType) &&
    (isDenimPath
      ? styleReady && paletteReady && Boolean(denimMaterialId && denimWashId)
      : isReportLookPath &&
        hasReportThemes &&
        Boolean(selectedMacroThemeId) &&
        paletteReady &&
        Boolean(trendSource) &&
        patternPathReady &&
        patternSpecsReady &&
        styleGarmentReady &&
        (outputType === "Pattern" || styleReady));
  const showTail =
    sessionLocked ||
    canGenerate ||
    showPatternSpecs ||
    showStyleGarment ||
    (isDenimPath &&
      styleReady &&
      paletteReady &&
      Boolean(denimMaterialId && denimWashId));

  const selectedStatus = resultItems.find(item => item.id === selectedResultId)
    ?.result.status;
  /** Only lock while a generate/regen request is in flight — not while viewing a failed look. */
  const regenBlocked = Boolean(busy);

  const forecastSilhouettePicker = showForecastReports ? (
    <ImageGeneratorForecastSilhouettePicker
      inline={showStyleFields}
      silhouetteSlides={forecastSilhouetteSlides}
      detailSlides={forecastDetailSlides}
      selectedSilhouetteId={forecastSilhouetteId}
      selectedDetailId={forecastDetailId}
      loading={forecastReportsLoading}
      disabled={sessionLocked}
      open={forecastSketchOpen}
      onOpenChange={setForecastSketchOpen}
      onSelectSilhouette={setForecastSilhouetteId}
      onSelectDetail={setForecastDetailId}
    />
  ) : null;

  return (
    <div
      className={`${
        !isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"
      } fixed transition-all duration-500 ease-in-out overflow-y-auto overflow-x-hidden h-[calc(100vh-80px)] ml-1 px-4 pt-1 pb-4 font-poppins bg-white text-secondary`}
    >
      <div className="w-full pb-16">
        <div className="mb-3 flex items-center text-xl sm:text-[22px]">
          <button
            type="button"
            onClick={() => setActiveTab("query")}
            className={`pr-3 font-semibold ${
              activeTab === "query" ? "text-secondary" : "text-tertiary"
            }`}
          >
            Report Query
          </button>
          <span className="font-semibold text-secondary">|</span>
          <button
            type="button"
            onClick={() => setActiveTab("tracker")}
            className={`px-3 font-semibold ${
              activeTab === "tracker" ? "text-secondary" : "text-tertiary"
            }`}
          >
            Report Tracker
          </button>
        </div>

        {activeTab === "query" ? (
          <p className="mb-5 text-sm text-secondary">
            Hey! I’m VYRA, your creative co-pilot. Let’s design your next
            winning piece.
          </p>
        ) : null}

        {activeTab === "tracker" ? (
          <ImageGeneratorTracker
            onOpen={(id: number) => {
              setActiveTab("query");
              openSession(id);
            }}
          />
        ) : (
          <>
            {sessionLocked && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-light bg-lightest px-3 py-2 text-sm text-secondary">
                <p>
                  {selectedStatus === "generating"
                    ? "This look is still generating. You can leave this page — we’ll notify you in the bell when it’s ready."
                    : selectedStatus === "failed"
                      ? "This look didn’t finish. Start a new generation to try again with different filters."
                      : "You’re viewing a saved look. Filters are locked. Start a new generation to change anything."}
                </p>
                <button
                  type="button"
                  onClick={startNewGeneration}
                  className="h-9 shrink-0 rounded-[100px] border border-[#737373] bg-white px-4 py-1 text-xs font-semibold text-secondary hover:bg-gray"
                >
                  New generation
                </button>
              </div>
            )}

            <div
              className={
                sessionLocked
                  ? "pointer-events-none select-none opacity-80"
                  : ""
              }
            >
              <div className={`${sectionClassName} pt-0`}>
                <div className={formFieldsShellClassName}>
                  <h2 className={`${sectionTitleClassName} mb-3`}>
                    Select Inputs
                  </h2>
                  <div className={fieldsRowClassName}>
                    <FieldBlock label="Genre">
                      <span className="mt-0 flex h-8 w-full min-w-0 items-center justify-between overflow-hidden rounded-[8px] border border-[#737373] bg-[#D3C0B626] py-1 pl-2 pr-2 text-left text-base">
                        <span className="block min-w-0 truncate whitespace-nowrap text-xs text-secondary">
                          {genre}
                        </span>
                      </span>
                    </FieldBlock>
                    <FieldBlock label="Market" required>
                      <FieldSelect
                        value={market}
                        placeholder="Select market"
                        onChange={setMarket}
                        options={MARKETS.map(item => ({
                          value: item,
                          label: item,
                        }))}
                      />
                    </FieldBlock>
                    <FieldBlock label="Category" required>
                      <FieldSelect
                        value={category}
                        placeholder="Select category"
                        onChange={setCategory}
                        options={CATEGORIES.map(item => ({
                          value: item,
                          label: categoryDisplayLabel(item),
                        }))}
                      />
                    </FieldBlock>
                    <FieldBlock label="Season" required>
                      <FieldSelect
                        value={seasonId}
                        placeholder={
                          !market || !category
                            ? "Select market and category"
                            : seasonsLoading
                              ? "Loading seasons..."
                              : seasons.length
                                ? "Select season"
                                : "No seasons for these filters"
                        }
                        disabled={!market || !category || seasonsLoading}
                        onChange={setSeasonId}
                        options={seasons.map(item => ({
                          value: item.id,
                          label: item.label,
                        }))}
                      />
                    </FieldBlock>
                  </div>
                  {market && category && seasonId && catalogLoading ? (
                    <p className={`${helperTextClassName} mt-3`}>
                      Loading report themes…
                    </p>
                  ) : null}
                  {market &&
                  category &&
                  !seasonsLoading &&
                  seasons.length === 0 ? (
                    <p className={`${helperTextClassName} mt-3`}>
                      No seasons for these filters.
                    </p>
                  ) : null}
                </div>
              </div>

              {showOutputType && (
                <div className={sectionClassName}>
                  <h2 className={sectionTitleClassName}>Setup</h2>
                  <p className={`${helperTextClassName} mt-1`}>
                    Let&apos;s set the brief.
                  </p>
                  <p className={`${helperTextClassName} mt-1 mb-3`}>
                    What do you want to design today - a full look, or just the
                    print?
                    <RequiredMark />
                  </p>
                  <div className="flex flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setOutputType("Style");
                        // Ethnic has no Starting Direction UI — lock Macro Theme now.
                        applyStartingDirection(
                          denimAllowed ? "" : "macro-theme"
                        );
                        setTrendSource("");
                        setSelectedSurfaceId("");
                        setSurfacePage(1);
                        setPatternChoice("");
                        setGarmentFormat("");
                        setSelectedEmbroideryType("");
                        setPatternProcess("");
                        setPatternRepeat("");
                        setPatternFollowUp("");
                        setPatternFollowUpText("");
                        setPatternOffset("");
                        setPatternDensity("");
                        setPatternScale("");
                        setPatternColorCount("");
                      }}
                      className={`${chipClassName(outputType === "Style")} mr-2 mb-2`}
                    >
                      Full Look
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOutputType("Pattern");
                        applyStartingDirection("");
                        setTrendSource("");
                        setSelectedSurfaceId("");
                        setSurfacePage(1);
                        setPatternChoice("");
                        setGarmentFormat("");
                        setSelectedEmbroideryType("");
                        setPatternProcess("");
                        setPatternRepeat("");
                        setPatternFollowUp("");
                        setPatternFollowUpText("");
                        setPatternOffset("");
                        setPatternDensity("");
                        setPatternScale("");
                        setPatternColorCount("");
                      }}
                      className={`${chipClassName(outputType === "Pattern")} mr-2 mb-2`}
                    >
                      Print
                    </button>
                  </div>
                </div>
              )}

              {showStyleFields && (
                <ImageGeneratorStyleFields
                  sectionTitle="Design your look"
                  market={market}
                  productType={productType}
                  productTypeOptions={productTypeOptions}
                  onProductTypeChange={next => {
                    setProductType(next);
                    setStyleType("");
                    setMaterial("");
                    setSleeve("");
                    setNeckline("");
                    setFit("");
                    setWash("");
                    setLength("");
                    // Western: reset direction so user re-picks Macro vs Denim.
                    // Ethnic: keep Macro Theme locked (no Starting Direction step).
                    applyStartingDirection(denimAllowed ? "" : "macro-theme");
                  }}
                  influence={influence}
                  onInfluenceChange={next => {
                    if (
                      outputType === "Style" &&
                      isForecastInfluence(next) &&
                      next !== influence
                    ) {
                      setForecastSketchAutoOpenToken(token => token + 1);
                    }
                    setInfluence(next);
                    if (!isUploadInfluence(next)) {
                      setSpecificationImageUrl("");
                      setSpecificationActive(false);
                      setSpecificationUploading(false);
                      setSpecificationResetKey(key => key + 1);
                    }
                    if (!isForecastInfluence(next)) {
                      setMaterial("");
                      setSleeve("");
                      setNeckline("");
                      setFit("");
                      setWash("");
                      setLength("");
                    }
                    applyStartingDirection(denimAllowed ? "" : "macro-theme");
                  }}
                  styleType={styleType}
                  styleTypeOptions={styleTypeOptions}
                  onStyleTypeChange={setStyleType}
                  material={material}
                  onMaterialChange={setMaterial}
                  sleeve={sleeve}
                  onSleeveChange={setSleeve}
                  neckline={neckline}
                  onNecklineChange={setNeckline}
                  length={length}
                  lengthOptions={lengthOptions}
                  onLengthChange={setLength}
                  fit={fit}
                  onFitChange={setFit}
                  wash={wash}
                  onWashChange={setWash}
                  visibleFollowUps={lookFollowUpFields}
                  specificationImageActive={specificationImageActive}
                  specificationSlot={
                    isUploadInfluence(influence) || forecastSilhouettePicker ? (
                      <>
                        {isUploadInfluence(influence) ? (
                          <ImageGeneratorSpecificationUpload
                            imageUrl={specificationImageUrl}
                            resetKey={specificationResetKey}
                            onImageUrlChange={setSpecificationImageUrl}
                            onActiveChange={setSpecificationActive}
                            onUploadingChange={setSpecificationUploading}
                          />
                        ) : null}
                        {forecastSilhouettePicker}
                      </>
                    ) : null
                  }
                />
              )}

              {!showStyleFields ? forecastSilhouettePicker : null}

              {showStartingDirection && (
                <div className={sectionClassName}>
                  <h2 className={`${sectionTitleClassName} mb-3`}>
                    Starting Direction
                    <RequiredMark />
                  </h2>

                  <ExclusivePills
                    options={startingDirectionOptions}
                    value={startingDirection}
                    onChange={applyStartingDirection}
                  />
                </div>
              )}

              {showMacro && (
                <div className={sectionClassName}>
                  {outputType === "Pattern" ? (
                    <h2 className={`${sectionTitleClassName} mb-3`}>
                      Design your Print
                    </h2>
                  ) : null}
                  <h2
                    className={`${sectionTitleClassName} ${
                      outputType === "Pattern" ? "mb-1" : "mb-3"
                    }`}
                  >
                    Select Macro Theme
                    <RequiredMark />
                  </h2>
                  <p className={`${helperTextClassName} mb-3`}>
                    Pick a macro theme from the forecast.
                  </p>
                  {hasReportThemes ? (
                    <ExclusivePills
                      options={(catalog?.macros || []).map(theme => ({
                        value: theme.id,
                        label: theme.name,
                      }))}
                      value={selectedMacroThemeId}
                      onChange={next => {
                        if (!next) {
                          setSelectedMacroThemeId("");
                          setShowColourReport(false);
                          return;
                        }
                        applyMacroTheme(next);
                      }}
                    />
                  ) : (
                    <p className={`${helperTextClassName} mt-1`}>
                      No extracted reports for these filters.
                    </p>
                  )}
                  {selectedMacroThemeId && colourReportSlides.length > 0 ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={showColourReport}
                        onClick={() => setShowColourReport(open => !open)}
                        className={viewReportButtonClassName}
                      >
                        {showColourReport ? "Hide report" : "View report"}
                      </button>
                      {showColourReport ? (
                        <ImageGeneratorReportSlider
                          slides={colourReportSlides}
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}

              {showPalette && (
                <ImageGeneratorPaletteSection
                  colourStories={paletteStories}
                  variant={isDenimPath ? "denim" : "default"}
                  customPalettes={customPalettes}
                  selectedPaletteIndex={selectedPaletteIndex}
                  onSelectStory={(index, id) => {
                    setSelectedPaletteIndex(index);
                    setSelectedColourStoryId(id);
                  }}
                  onSelectCustom={index => {
                    setSelectedPaletteIndex(index);
                    setSelectedColourStoryId("");
                  }}
                  onEditCustom={customIndex => {
                    openEditCustomPalette(customIndex);
                  }}
                  onClear={clearPalette}
                  makeOwnOpen={makeOwnOpen}
                  makeOwnRef={makeOwnRef}
                  onToggleMakeOwn={openMakeOwnPicker}
                  makeOwnSlots={makeOwnSlots}
                  makeOwnSlotIndex={makeOwnSlotIndex}
                  onSelectMakeOwnSlot={setMakeOwnSlotIndex}
                  onChangeMakeOwnSlot={handleMakeOwnSlotChange}
                  onAddMakeOwnColour={handleMakeOwnAdd}
                  onAddMakeOwnSlot={handleAddMakeOwnSlot}
                  onRemoveMakeOwnSlot={handleRemoveMakeOwnSlot}
                  onUploadClick={() => uploadInputRef.current?.click()}
                  paletteExtracting={paletteExtracting}
                  uploadInputRef={uploadInputRef}
                  onUpload={handleUploadPalette}
                />
              )}

              {isDenimPath && (
                <ImageGeneratorDenimSections
                  materials={denimCatalog.materials}
                  washes={denimCatalog.washes}
                  selectedMaterialId={denimMaterialId}
                  selectedWashId={denimWashId}
                  materialPreviews={denimPreviews}
                  washPreviews={denimPreviews}
                  showMaterials={showDenimMaterials}
                  showWashes={showDenimWashes}
                  onSelectMaterial={id =>
                    setDenimMaterialId(prev => (prev === id ? "" : id))
                  }
                  onSelectWash={id =>
                    setDenimWashId(prev => (prev === id ? "" : id))
                  }
                />
              )}

              {showTrendSource && (
                <div className={sectionClassName}>
                  <h2 className={`${sectionTitleClassName} mb-3`}>
                    Select
                    <RequiredMark />
                  </h2>
                  <ExclusivePills
                    options={[
                      { value: "pattern", label: "Pattern" },
                      { value: "surface", label: "Surface Trend" },
                    ]}
                    value={trendSource}
                    onChange={applyTrendSource}
                    allowDeselect={false}
                  />
                  {!surfacesLoading &&
                  surfaceSlides.length === 0 &&
                  trendSource === "surface" ? (
                    <p className={`${helperTextClassName} mt-2`}>
                      No surface-report slides for these filters. Choose Pattern
                      instead.
                    </p>
                  ) : null}
                </div>
              )}

              {showSlideReports && (
                <ImageGeneratorSlideReports
                  slides={surfaceSlides}
                  page={surfacePage}
                  selectedId={selectedSurfaceId}
                  loading={surfacesLoading}
                  disabled={sessionLocked}
                  onPageChange={setSurfacePage}
                  onSelect={setSelectedSurfaceId}
                />
              )}

              {showPatternChoice && (
                <div className={sectionClassName}>
                  <h2 className={sectionTitleClassName}>
                    Select Pattern Trend
                    <RequiredMark />
                  </h2>
                  <p className={`${helperTextClassName} mt-1 mb-3`}>
                    Got a favourite print from this story, or want me to suggest
                    a few?
                  </p>
                  <ExclusivePills
                    options={PATTERN_CHOICE_OPTIONS}
                    value={patternChoice}
                    onChange={setPatternChoice}
                    allowDeselect={false}
                  />
                  {patternChoice === "recommend" ? (
                    <>
                      <p className={`${helperTextClassName} mt-3`}>
                        We&apos;ve selected one pattern tile for you.
                      </p>
                      {suggestedPreferenceAsset ? (
                        <div className="mt-3 w-full max-w-[165px]">
                          <ImageGeneratorPreferenceTile
                            asset={suggestedPreferenceAsset}
                            previewUrl={
                              assetPreviews[suggestedPreferenceAsset.stem] ||
                              (/^https?:\/\//i.test(
                                suggestedPreferenceAsset.url
                              )
                                ? suggestedPreferenceAsset.url
                                : undefined)
                            }
                            isSelected={true}
                            choiceLocked={false}
                            isMuted={false}
                            onToggle={togglePreference}
                            caption="Selected pattern tile"
                            aspectClassName="aspect-[2/3]"
                          />
                        </div>
                      ) : null}
                    </>
                  ) : null}{" "}
                </div>
              )}

              {showMicroTheme && (
                <div className={sectionClassName}>
                  <p className={`${helperTextClassName} mb-1`}>
                    {category === "Western RTW"
                      ? "Nice pick! Now narrow it down"
                      : "Nice pick! Here's where it leads"}
                  </p>
                  <h2 className={sectionTitleClassName}>
                    Select Micro-theme
                    <RequiredMark />
                  </h2>
                  <p className={`${helperTextClassName} mt-1 mb-3`}>
                    Pick a micro theme from this report.
                  </p>
                  <ExclusivePills
                    options={patternTrends.map(micro => ({
                      value: micro.id,
                      label: micro.name,
                    }))}
                    value={selectedPatternTrendId}
                    onChange={setSelectedPatternTrendId}
                  />
                  {selectedPatternTrendId && microReportSlides.length > 0 ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={showMicroReport}
                        onClick={() => setShowMicroReport(open => !open)}
                        className={viewReportButtonClassName}
                      >
                        {showMicroReport ? "Hide report" : "View report"}
                      </button>
                      {showMicroReport ? (
                        <ImageGeneratorReportSlider
                          slides={microReportSlides}
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}

              {showPrefs && (
                <div className={sectionClassName}>
                  <h2 className={`${sectionTitleClassName} mb-1`}>
                    Select Preferences
                    <RequiredMark />
                  </h2>
                  <p className={`${helperTextClassName} mb-4`}>
                    {patternChoice === "choose"
                      ? "Pick one pattern tile."
                      : "We’ll pick one pattern from this trend at random and send it with the style prompt."}
                  </p>

                  {preferenceAssets.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-3 sm:gap-4">
                        {pagedPreferenceAssets.map(renderPreferenceTile)}
                      </div>
                      {preferencePageCount > 1 ? (
                        <div className="mt-4 flex items-center justify-end gap-2 text-xs text-secondary">
                          <button
                            type="button"
                            onClick={() =>
                              setPreferencePage(p => Math.max(1, p - 1))
                            }
                            disabled={preferencePage === 1}
                            className="rounded border border-[#737373] px-2 py-1 hover:bg-gray disabled:opacity-40"
                            aria-label="Previous preference page"
                          >
                            &lt;
                          </button>
                          <div className="w-[72px]">
                            <FieldSelect
                              value={String(preferencePage)}
                              onChange={next => setPreferencePage(Number(next))}
                              options={Array.from(
                                { length: preferencePageCount },
                                (_, index) => ({
                                  value: String(index + 1),
                                  label: String(index + 1),
                                })
                              )}
                            />
                          </div>
                          <span>of {preferencePageCount}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setPreferencePage(p =>
                                Math.min(preferencePageCount, p + 1)
                              )
                            }
                            disabled={preferencePage === preferencePageCount}
                            className="rounded border border-[#737373] px-2 py-1 hover:bg-gray disabled:opacity-40"
                            aria-label="Next preference page"
                          >
                            &gt;
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="py-8 text-center text-sm text-tertiary">
                      No preference assets listed for this report.
                    </div>
                  )}
                </div>
              )}

              {showStyleGarment && (
                <>
                  <div className={sectionClassName}>
                    <h2 className={sectionTitleClassName}>
                      Select Render Format
                      <RequiredMark />
                    </h2>
                    <p className={`${helperTextClassName} mt-1 mb-3`}>
                      How should it render on the garment?
                    </p>
                    <ExclusivePills
                      options={GARMENT_FORMATS.map(fmt => ({
                        value: fmt,
                        label: fmt,
                      }))}
                      value={garmentFormat}
                      onChange={next => {
                        setGarmentFormat(next);
                        if (next !== "Print") {
                          setPatternProcess("");
                        }
                        if (next !== "Embroidery") {
                          setSelectedEmbroideryType("");
                        }
                      }}
                      allowDeselect={false}
                    />
                  </div>
                  {garmentFormat === "Print" && (
                    <div className={sectionClassName}>
                      <h2 className={`${sectionTitleClassName} mb-3`}>
                        Can we get more specific?
                        <RequiredMark />
                      </h2>
                      <ExclusivePills
                        options={PATTERN_PROCESS_OPTIONS}
                        value={patternProcess}
                        onChange={setPatternProcess}
                        allowDeselect={false}
                      />
                    </div>
                  )}
                  {garmentFormat === "Embroidery" && (
                    <div className={sectionClassName}>
                      <h2 className={`${sectionTitleClassName} mb-3`}>
                        Select Embroidery Type
                        <RequiredMark />
                      </h2>
                      <ImageGeneratorEmbroideryType
                        value={selectedEmbroideryType}
                        onChange={setSelectedEmbroideryType}
                        showTitle={false}
                      />
                    </div>
                  )}
                </>
              )}

              {showPatternSpecs && (
                <ImageGeneratorPatternSpecs
                  garmentFormat={garmentFormat}
                  onGarmentFormatChange={setGarmentFormat}
                  patternRepeat={patternRepeat}
                  onPatternRepeatChange={setPatternRepeat}
                  patternFollowUp={patternFollowUp}
                  onPatternFollowUpChange={setPatternFollowUp}
                  patternFollowUpText={patternFollowUpText}
                  onPatternFollowUpTextChange={setPatternFollowUpText}
                  patternOffset={patternOffset}
                  onPatternOffsetChange={setPatternOffset}
                  patternDensity={patternDensity}
                  onPatternDensityChange={setPatternDensity}
                  patternScale={patternScale}
                  onPatternScaleChange={setPatternScale}
                  patternColorCount={patternColorCount}
                  onPatternColorCountChange={setPatternColorCount}
                  patternProcess={patternProcess}
                  onPatternProcessChange={setPatternProcess}
                  embroideryType={selectedEmbroideryType}
                  onEmbroideryTypeChange={setSelectedEmbroideryType}
                />
              )}

              {showTail && (
                <>
                  <div className={`${sectionClassName} border-b-0`}>
                    <h2 className={`${sectionTitleClassName} mb-1`}>
                      Add input
                    </h2>
                    <p className={`${helperTextClassName} mb-2`}>
                      Anything else specific in mind? (Totally optional)
                    </p>
                    <textarea
                      value={additionalPrompt}
                      onChange={e => setAdditionalPrompt(e.target.value)}
                      disabled={Boolean(busy)}
                      placeholder={
                        outputType === "Pattern"
                          ? 'e.g. "Add aari embroidery on the floral motif of the pattern," "make it more minimal with increased negative space"'
                          : 'e.g. "Add a dori tie-up at the back," "Keep the yoke plain"'
                      }
                      className="w-full min-h-[100px] p-3 rounded-lg border border-[#737373] text-sm text-secondary focus:outline-none resize-y disabled:bg-lightest"
                    />
                  </div>

                  {!sessionLocked && (
                    <>
                      <div className="mt-6 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            if (hasIncompleteStyleDropdowns) {
                              setConfirmIncompleteDropdowns(true);
                              return;
                            }
                            void handleGenerate();
                          }}
                          disabled={Boolean(busy) || !canGenerate}
                          className={`${mutedButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                        >
                          {busy === "generate" ? "Queuing..." : "Generate"}
                        </button>
                      </div>
                      {isDenimPath && (
                        <p className={`${helperTextClassName} mt-2`}>
                          Note: Pattern and Macro-theme selection is skipped for
                          the denim starting direction.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {sessionLocked && resultItems.length > 0 && (
              <ImageGeneratorInlineResult
                items={resultItems}
                selectedId={selectedResultId}
                onSelect={id => {
                  if (!busy) {
                    setSelectedResultId(id);
                  }
                }}
                onRegenerate={handleRegenerate}
                onRecordUpdated={updateSession}
                regenerating={regenBlocked}
                scrollKey={resultScrollKey}
              />
            )}
          </>
        )}
      </div>
      {confirmIncompleteDropdowns &&
        createPortal(
          <div className="fixed inset-0 z-[220] flex items-center justify-center font-poppins">
            <div
              className="fixed inset-0 bg-black opacity-50"
              onClick={() => setConfirmIncompleteDropdowns(false)}
            />
            <div
              className="relative z-10 w-full max-w-sm rounded-2xl border border-[#D0D0D0] bg-white p-6 shadow-lg"
              role="dialog"
              aria-modal="true"
              aria-labelledby="incomplete-dropdowns-confirm-title"
            >
              <h2
                id="incomplete-dropdowns-confirm-title"
                className="text-xl font-semibold text-primary"
              >
                {INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_TITLE}
              </h2>
              <p className="mt-2 text-sm text-secondary">
                {INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_BODY}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmIncompleteDropdowns(false)}
                  className={outlineButtonClassName}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    setConfirmIncompleteDropdowns(false);
                    void handleGenerate();
                  }}
                  className={`${submitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ImageGenerator;
